import { useQuery } from "@tanstack/react-query";
import { useContext, useMemo, useState } from "react";
import { useDataProvidersApi } from "../../../../../hooks";
import { CustomSwitch } from "../../../../../ui/CustomSwitch";
import { MapDataContext } from "../../../context";
import { useMapFilters } from "../../../hooks/mapDataAndFilters";
import { InsightsContainer } from "../../../ui/MapControlsV2";
import { CATEGORY_COLORS, type CountsCardCategory, CountsCardView } from "./ui";

export const ThirdPartyEmissionInsights = () => {
    const emissionData = useContext(MapDataContext);
    const dataProvidersApiClient = useDataProvidersApi();
    const [plotMode, setPlotMode] = useState<"count" | "total_volume">("count");
    const { filterState } = useMapFilters("mainMap");

    const dataProvidersQuery = useQuery({
        queryKey: ["dataProvidersFullTree"],
        queryFn: async () => {
            const response = await dataProvidersApiClient.dataProvidersList();
            return response.results;
        },
        refetchOnWindowFocus: false,
        staleTime: 0,
    });

    const stats = useMemo(() => {
        const data: {
            [key: string]: {
                id: string;
                name: string;
                emissionCount: number;
                emissionSum: number;
            };
        } = {};
        if (
            !filterState.emissions.showEmissions ||
            filterState.emissions.emissionGroupFiltersV2.thirdParty.length === 0
        ) {
            return [];
        }
        for (const e of emissionData.filteredByContext.thirdPartyEmissions) {
            if (!data[e.providerId]) {
                data[e.providerId] = {
                    id: e.providerId,
                    name: "",
                    emissionCount: 0,
                    emissionSum: 0,
                };
            }
            data[e.providerId].emissionCount += 1;
            data[e.providerId].emissionSum += e.detectedRate;
        }
        return Object.values(data).map((data) => {
            return {
                ...data,
                name: Array.isArray(dataProvidersQuery.data)
                    ? dataProvidersQuery.data.find((p) => p.id === data.id)
                          ?.name
                    : undefined,
            };
        });
    }, [filterState, emissionData.filteredByContext, dataProvidersQuery.data]);

    // One category per provider, largest first, coloured by rank so the
    // biggest contributors get the most distinct colours.
    const categories = useMemo((): CountsCardCategory[] => {
        return [...stats]
            .map((p) => ({
                key: p.id,
                label: p.name || "Unknown provider",
                // The volume mode reports kg/h, while rates are stored in g/h.
                count:
                    plotMode === "count"
                        ? p.emissionCount
                        : Math.round(p.emissionSum / 1000),
            }))
            .filter((p) => p.count > 0)
            .sort((a, b) => b.count - a.count)
            .map((p, i) => ({
                ...p,
                color: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
            }));
    }, [stats, plotMode]);

    if (
        !filterState.emissions.showEmissions ||
        filterState.emissions.emissionGroupFiltersV2.thirdParty.length === 0
    ) {
        return null;
    }

    return (
        <InsightsContainer>
            <div className="flex items-center justify-between">
                <p className="font-bold text-sm">Third Party Emissions</p>
                <div className="flex items-center">
                    <span className="text-xs text-gray-600 mr-1">
                        Count/volume
                    </span>
                    <CustomSwitch
                        size="xs"
                        checked={plotMode === "total_volume"}
                        onChange={() =>
                            setPlotMode((v) =>
                                v === "count" ? "total_volume" : "count",
                            )
                        }
                    />
                </div>
            </div>
            <CountsCardView
                data={categories}
                loading={emissionData.loading || dataProvidersQuery.isFetching}
                totalLabel={
                    plotMode === "count" ? "emissions in view" : "kg/h in view"
                }
            />
        </InsightsContainer>
    );
};
